import { type RefObject, useCallback, useLayoutEffect } from 'react'

interface AutoGrowOptions {
  maxRows?: number
}

const fitToContent = (textarea: HTMLTextAreaElement, { maxRows }: AutoGrowOptions) => {
  if (textarea.offsetParent === null) {
    return
  }
  const style = window.getComputedStyle(textarea)
  const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
  const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
  const limit = maxRows === undefined ? Infinity : parseFloat(style.lineHeight) * maxRows + padding + borders
  textarea.style.height = 'auto'
  const contentHeight = textarea.scrollHeight + borders
  textarea.style.height = `${Math.min(contentHeight, limit)}px`
  textarea.style.overflowY = textarea.scrollHeight > textarea.clientHeight ? 'auto' : 'hidden'
}

export const useAutoGrowTextarea = (ref: RefObject<HTMLTextAreaElement | null>, value: unknown, options: AutoGrowOptions = {}) => {
  const { maxRows } = options

  const refit = useCallback(() => {
    if (ref.current) {
      fitToContent(ref.current, { maxRows })
    }
  }, [ref, maxRows])

  useLayoutEffect(() => {
    refit()
  }, [refit, value])

  useLayoutEffect(() => {
    const textarea = ref.current
    if (!textarea || typeof ResizeObserver === 'undefined') {
      return
    }
    let lastWidth = textarea.offsetWidth
    let wasHidden = textarea.offsetParent === null
    const observer = new ResizeObserver(() => {
      const hidden = textarea.offsetParent === null
      if (textarea.offsetWidth === lastWidth && hidden === wasHidden) {
        return
      }
      lastWidth = textarea.offsetWidth
      wasHidden = hidden
      fitToContent(textarea, { maxRows })
    })
    observer.observe(textarea)
    return () => observer.disconnect()
  }, [ref, maxRows])
}
